import { useEffect, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClassroomDto, ClassroomSettingsDto } from '../api/types.js';
import { useApi } from '../api/ApiProvider.js';
import type { UpdateClassroomSettingsBody } from '../api/home.js';
import { isoDate } from '../lib/localDate.js';
import { useClassroomDay } from '../hooks/useClassroomDay.js';
import { ResetSettingsButton } from './ResetSettingsButton.js';
import styles from './settingsForm.module.css';

export function QuestionPageSettings({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const classroomId = classroom.id;

  const settingsQuery = useQuery<ClassroomSettingsDto>({
    queryKey: ['classroom-settings', classroomId],
    queryFn: () => api.home.getSettings(classroomId),
  });

  const today = isoDate();
  const resetAll = useMutation({
    mutationFn: async () => {
      // Show flags + the weekly question live in settings; the daily question
      // + answer live in today's per-day record.
      const next = await api.home.patchSettings(classroomId, {
        questionShowDaily: false,
        questionShowWeekly: false,
        questionWeekly: '',
        questionWeeklyAnswer: '',
      });
      await api.dayHistory.putDay(classroomId, today, {
        questionDaily: '',
        questionDailyAnswer: '',
      });
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData<ClassroomSettingsDto>(['classroom-settings', classroomId], next);
      void queryClient.invalidateQueries({ queryKey: ['classroom-day', classroomId, today] });
    },
  });

  return (
    <div className={`glass glass--solid ${styles.panel}`}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.heading}>Question</h2>
          <p className={styles.description}>
            Daily and weekly prompts. Answers reveal when you tap the prompt during morning meeting
            and are stored encrypted.
          </p>
        </div>
        <ResetSettingsButton
          pageLabel="Question"
          onReset={() => resetAll.mutate()}
          disabled={resetAll.isPending}
        />
      </header>

      <div className={styles.twoCol}>
        <QuestionFields
          classroomId={classroomId}
          settings={settingsQuery.data}
          isLoading={settingsQuery.isLoading}
          onSaved={(next) => {
            queryClient.setQueryData<ClassroomSettingsDto>(
              ['classroom-settings', classroomId],
              next,
            );
          }}
        />
      </div>
    </div>
  );
}

interface QuestionFieldsProps {
  classroomId: string;
  settings: ClassroomSettingsDto | undefined;
  isLoading: boolean;
  onSaved: (next: ClassroomSettingsDto) => void;
}

function QuestionFields({ classroomId, settings, isLoading, onSaved }: QuestionFieldsProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  // The DAILY question + answer are per-day (today's classroom_day, carried
  // forward); the WEEKLY question + answer and the show flags stay in settings.
  const today = isoDate();
  const day = useClassroomDay(classroomId);

  // Local copies for editable text — toggles save immediately so they don't need local state.
  const [daily, setDaily] = useState('');
  const [dailyAnswer, setDailyAnswer] = useState('');
  const [weekly, setWeekly] = useState(settings?.questionWeekly ?? '');
  const [weeklyAnswer, setWeeklyAnswer] = useState(settings?.questionWeeklyAnswer ?? '');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    if (settings) {
      setWeekly(settings.questionWeekly);
      setWeeklyAnswer(settings.questionWeeklyAnswer);
    }
  }, [settings, classroomId]);
  useEffect(() => {
    if (day.data) {
      setDaily(day.data.questionDaily);
      setDailyAnswer(day.data.questionDailyAnswer);
    }
  }, [day.data, classroomId]);

  const save = useMutation({
    mutationFn: (patch: UpdateClassroomSettingsBody) => api.home.patchSettings(classroomId, patch),
    onMutate: () => setStatus('saving'),
    onSuccess: (next) => {
      onSaved(next);
      setStatus('saved');
    },
    onError: () => setStatus('error'),
  });

  const saveDay = useMutation({
    mutationFn: (patch: { questionDaily?: string; questionDailyAnswer?: string }) =>
      api.dayHistory.putDay(classroomId, today, patch),
    onMutate: () => setStatus('saving'),
    onSuccess: (nextDay) => {
      queryClient.setQueryData(['classroom-day', classroomId, today], nextDay);
      setStatus('saved');
    },
    onError: () => setStatus('error'),
  });

  const blurIfChanged = (key: keyof ClassroomSettingsDto, local: string) => () => {
    if (!settings) return;
    if (settings[key] === local) return;
    save.mutate({ [key]: local } as UpdateClassroomSettingsBody);
  };

  const blurDayIfChanged = (key: 'questionDaily' | 'questionDailyAnswer', local: string) => () => {
    if (!day.data) return;
    if (day.data[key] === local) return;
    saveDay.mutate({ [key]: local });
  };

  const toggle =
    (key: 'questionShowDaily' | 'questionShowWeekly') => (e: ChangeEvent<HTMLInputElement>) => {
      save.mutate({ [key]: e.target.checked } as UpdateClassroomSettingsBody);
    };

  return (
    <>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Question of the day</h3>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={settings?.questionShowDaily ?? false}
            disabled={isLoading}
            onChange={toggle('questionShowDaily')}
          />
          Show on the Question page
        </label>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="question-daily">
            Prompt
          </label>
          <textarea
            id="question-daily"
            className={`${styles.textarea} ${styles.textareaCompact}`}
            value={daily}
            placeholder="e.g. What's your favorite season and why?"
            maxLength={500}
            disabled={day.isLoading}
            rows={2}
            onChange={(e) => setDaily(e.target.value)}
            onBlur={blurDayIfChanged('questionDaily', daily)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="question-daily-answer">
            Answer (tap to reveal)
          </label>
          <textarea
            id="question-daily-answer"
            className={`${styles.textarea} ${styles.textareaCompact}`}
            value={dailyAnswer}
            placeholder="Optional."
            maxLength={500}
            disabled={day.isLoading}
            rows={2}
            onChange={(e) => setDailyAnswer(e.target.value)}
            onBlur={blurDayIfChanged('questionDailyAnswer', dailyAnswer)}
          />
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Question of the week</h3>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={settings?.questionShowWeekly ?? false}
            disabled={isLoading}
            onChange={toggle('questionShowWeekly')}
          />
          Show on the Question page
        </label>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="question-weekly">
            Prompt
          </label>
          <textarea
            id="question-weekly"
            className={`${styles.textarea} ${styles.textareaCompact}`}
            value={weekly}
            placeholder="e.g. One kind thing you saw this week?"
            maxLength={500}
            disabled={isLoading}
            rows={2}
            onChange={(e) => setWeekly(e.target.value)}
            onBlur={blurIfChanged('questionWeekly', weekly)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="question-weekly-answer">
            Answer (tap to reveal)
          </label>
          <textarea
            id="question-weekly-answer"
            className={`${styles.textarea} ${styles.textareaCompact}`}
            value={weeklyAnswer}
            placeholder="Optional."
            maxLength={500}
            disabled={isLoading}
            rows={2}
            onChange={(e) => setWeeklyAnswer(e.target.value)}
            onBlur={blurIfChanged('questionWeeklyAnswer', weeklyAnswer)}
          />
        </div>
      </div>

      {status === 'saving' ? <span className={styles.statusLine}>Saving…</span> : null}
      {status === 'saved' ? <span className={styles.statusLine}>Saved</span> : null}
      {status === 'error' ? (
        <span className={`${styles.statusLine} ${styles.statusError}`}>Save failed</span>
      ) : null}
    </>
  );
}
